import Vue from "vue";
import { Store } from "vuex";
import { Action, Module, Mutation, VuexModule, getModule } from "vuex-module-decorators";

import { isModelStale, processCoreModelResponse } from "@/store/-utils";
import FollowerApi from "@/store/follower/-api";
import FollowsApi from "@/store/follower/-follows-api";
import { getRecommendationsStore } from "@/store/recommendations";
import logger, { logLevel } from "@/utils/logger";

export const DEFAULT_FOLLOWER_LIMIT = 30;
export const DEFAULT_RECOMMENDED_PROFILES_LIMIT = 3;

const followsFeed = "followedProfiles";

@Module({
  name: "follower",
  stateFactory: true,
  namespaced: true,
})
export default class FollowerStore extends VuexModule {
  recommendedProfilesSuggestions: SearchSuggestion[] = [];
  followedProfiles: FollowMap = {};
  meta: FollowMeta | null = null;
  ids: string[] = [];

  loggedOutFollowProfile = (process.client ? sessionStorage.getItem("follow-profile") : "") ?? "";

  get getFeedHasMorePages() {
    return () => this.meta === null || Boolean(this.meta.nextUrl);
  }

  get hasFeedBeenFetched() {
    return this.meta !== null && this.ids.length > 0;
  }

  @Action({ rawError: true })
  async fetchRecommendedProfiles(limit = DEFAULT_RECOMMENDED_PROFILES_LIMIT) {
    await getRecommendationsStore(this.store).fetchRecommendedProfiles(limit);
    const suggestions = getRecommendationsStore(this.store).recommendedProfilesSuggestions;

    if (this.store.$auth.loggedIn) {
      const api = new FollowerApi(this.store.$axios);
      const response = await api.getRecommenedProfile(limit);
      await processCoreModelResponse(this.store, response.data);

      const mergedSuggestions = [
        ...suggestions,
        ...response.data.profiles.map((item) => {
          return {
            objectID: item.id,
            query: item.displayName,
            avatarUrl: item.avatarUrl ? `${item.avatarUrl}?v=0&format=auto&w=100&h=100&fit=crop&q=80` : "",
          };
        }),
      ];
      this.setRecommendedProfilesSuggestions(mergedSuggestions.reverse());

      // set profile suggestions to use in autocomplete
    } else {
      this.setRecommendedProfilesSuggestions(suggestions);
    }
  }

  @Action({ rawError: true })
  async fetchFollowingCreators({
    data = {},
    overwrite = false,
  }: {
    data?: Parameters<FollowsApi["get"]>[0];
    overwrite?: boolean;
  }) {
    if (!this.getFeedHasMorePages && overwrite === false) {
      return;
    }

    const api = new FollowsApi(this.store.$axios);
    const meta = this.meta;

    if (this.ids.length > 0 && this.getFeedHasMorePages()) {
      data.beforeId = meta?.lastId;
      data.limit = meta?.limit;
    }

    const response = await api.get({ ...data });

    if (response.data.follows?.length > 0) {
      await processCoreModelResponse(this.store, response.data);
      this.setFollowsFeed({
        ids: response.data.follows.map(({ profileId }) => profileId),
        meta: response.data.meta,
        overwrite,
      });
    }
  }

  @Action({ rawError: true })
  async fetchFollowsByProfileId(profileId: string) {
    // Skip refreshing recent models
    if (!isModelStale(this.followedProfiles[profileId])) {
      return;
    }

    try {
      const api = new FollowsApi(this.store.$axios);
      const response = await api.getOne(profileId);

      await processCoreModelResponse(this.store, response.data);
    } catch (error) {}
  }

  @Action({ rawError: true })
  async follow(profileId: string) {
    const api = new FollowsApi(this.store.$axios);
    const response = await api.followCreatorAggregate(profileId);
    if (response.data.follows && response.data.follows.length > 0) {
      const follow = response.data.follows[0];
      this.addFollow({ follow });
    }
  }

  @Action({ rawError: true })
  async unfollow(profileId: string) {
    const api = new FollowsApi(this.store.$axios);
    await api.unfollowCreatorAggregate(profileId);
    this.removeFollow(profileId);
  }

  @Mutation
  setRecommendedProfilesSuggestions(profiles: SearchSuggestion[]) {
    Vue.set(this, "recommendedProfilesSuggestions", profiles);
  }

  @Mutation
  setFollowedProfiles({ follows }: { follows: FollowMap }) {
    Vue.set(this, followsFeed, Object.assign({}, this.followedProfiles, follows));
  }

  @Mutation
  setFollowsFeed({ ids, meta, overwrite = false }: { ids: string[]; meta?: FollowMeta; overwrite: boolean }) {
    // Add items to feed
    const start = overwrite ? 0 : this.ids.length;
    const length = overwrite ? this.ids.length : 0;
    this.ids.splice(start, length, ...ids);

    if (meta !== undefined) {
      Vue.set(this, "meta", meta);
    }
  }

  @Mutation
  removeFollow(profileId: string) {
    const idIndex = this.ids.findIndex((id) => id === profileId);
    if (idIndex !== -1) {
      this.ids.splice(idIndex, 1);
    }
    Vue.delete(this.followedProfiles, profileId);
  }

  @Mutation
  addFollow({ follow }: { follow: Follow }) {
    Vue.set(this.followedProfiles, follow.profileId, follow);
    this.ids.unshift(follow.profileId);
  }

  @Mutation
  setLoggedOutFollowProfile(profileId: string) {
    this.loggedOutFollowProfile = profileId;
  }
}

export const getFollowerStore = (store: Store<unknown>) => {
  return getModule(FollowerStore, store);
};

export const followerStorePlugin = (store: Store<unknown>) => {
  store.subscribeAction(({ type }) => {
    switch (type) {
      case "likes/createLike":
      case "likes/deleteLike":
        getFollowerStore(store).fetchRecommendedProfiles(DEFAULT_RECOMMENDED_PROFILES_LIMIT);
        return;
      default:
        logger(logLevel.DEBUG, "[Follower Store] Ignoring Action", { type });
    }
  });
};
